import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "../components/common/PageHeader";
import useStore from "../store/useStore";
import api from "../services/api";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

const SuperAdminDashboard = () => {
  const { toast } = useStore();
  const [loading, setLoading] = useState(false);
  const [processingAction, setProcessingAction] = useState("");
  const [approvingId, setApprovingId] = useState(null);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [approvalForm, setApprovalForm] = useState({
    subscriptionPlan: "",
    subscriptionCycle: "Monthly",
    subscriptionAmount: 0,
    subscriptionCurrency: "GMD",
    subscriptionStart: new Date().toISOString().split("T")[0],
    subscriptionEnd: "",
    paymentContactMethod: "WhatsApp",
    paymentEvidenceUrl: "",
    subscriptionNotes: ""
  });
  const [overview, setOverview] = useState({
    metrics: {},
    recentCompanies: [],
    recentUsers: []
  });

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/overview");
      setOverview(res.data?.data || { metrics: {}, recentCompanies: [], recentUsers: [] });
    } catch (error) {
      console.error("SuperAdmin overview error:", error);
      toast(error.response?.data?.message || "Failed to load superadmin dashboard", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await api.get("/companies");
      setCompanies(res.data?.data || []);
    } catch (error) {
      console.error("Load companies error:", error);
    }
  }, []);

  const fetchSubscriptionPlans = useCallback(async () => {
    try {
      const res = await api.get("/subscription-plans");
      setSubscriptionPlans(res.data?.data || []);
    } catch (error) {
      console.error("Load subscription plans error:", error);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    fetchCompanies();
    fetchSubscriptionPlans();
  }, [fetchCompanies, fetchOverview, fetchSubscriptionPlans]);

  const metrics = useMemo(() => overview.metrics || {}, [overview.metrics]);

  const cards = useMemo(() => ([
    { label: "Companies", value: metrics.totalCompanies || 0, tone: "bg-teal-50 text-teal-700 border-teal-100", icon: BusinessOutlinedIcon, hint: "Registered organizations" },
    { label: "Users", value: metrics.totalUsers || 0, tone: "bg-cyan-50 text-cyan-700 border-cyan-100", icon: GroupOutlinedIcon, hint: "All user accounts" },
    { label: "Revenue", value: `GMD ${Number(metrics.totalRevenue || 0).toLocaleString()}`, tone: "bg-amber-50 text-amber-700 border-amber-100", icon: PaymentsOutlinedIcon, hint: "Total recorded payments" },
    { label: "Occupancy", value: `${metrics.occupancyRate || 0}%`, tone: "bg-indigo-50 text-indigo-700 border-indigo-100", icon: InsightsOutlinedIcon, hint: "Occupied rental ratio" },
    { label: "Assets", value: metrics.totalAssets || 0, tone: "bg-emerald-50 text-emerald-700 border-emerald-100", icon: HomeWorkOutlinedIcon, hint: "Total properties/assets" },
    { label: "Tenants", value: metrics.totalTenants || 0, tone: "bg-lime-50 text-lime-700 border-lime-100", icon: PeopleOutlineOutlinedIcon, hint: "Active tenant records" },
    { label: "Payments", value: metrics.totalPayments || 0, tone: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100", icon: ReceiptLongOutlinedIcon, hint: "Captured payment entries" },
    { label: "Maintenance", value: metrics.totalMaintenance || 0, tone: "bg-slate-50 text-slate-700 border-slate-100", icon: BuildOutlinedIcon, hint: "Maintenance records" }
  ]), [metrics]);

  const pendingCompanies = useMemo(
    () => companies
      .filter((company) => Number(company.active) === 0 || String(company.subscriptionStatus || "").toLowerCase() !== "active")
      .slice(0, 8),
    [companies]
  );

  const computeEndDate = (startDate, cycle) => {
    if (!startDate) return "";
    const start = new Date(startDate);
    if (Number.isNaN(start.getTime())) return "";

    const end = new Date(start);
    if (cycle === "Yearly") {
      end.setFullYear(end.getFullYear() + 1);
    } else {
      end.setMonth(end.getMonth() + 1);
    }
    end.setDate(end.getDate() - 1);
    return end.toISOString().split("T")[0];
  };

  const getPlanAmount = (planName, cycle) => {
    const plan = subscriptionPlans.find((item) => item.name === planName);
    if (!plan) return 0;
    return cycle === "Yearly" ? Number(plan.yearlyPrice || 0) : Number(plan.monthlyPrice || 0);
  };

  const openApprovalModal = (company) => {
    const today = new Date().toISOString().split("T")[0];
    const defaultPlan = subscriptionPlans[0]?.name || "";
    const plan = company.subscriptionPlan || defaultPlan;
    const cycle = company.subscriptionCycle || "Monthly";
    const amount = Number(company.subscriptionAmount || getPlanAmount(plan, cycle));
    const start = company.subscriptionStart ? company.subscriptionStart.split("T")[0] : today;
    const end = company.subscriptionEnd ? company.subscriptionEnd.split("T")[0] : computeEndDate(start, cycle);

    setSelectedCompany(company);
    setApprovalForm({
      subscriptionPlan: plan,
      subscriptionCycle: cycle,
      subscriptionAmount: amount,
      subscriptionCurrency: "GMD",
      subscriptionStart: start,
      subscriptionEnd: end,
      paymentContactMethod: company.paymentContactMethod || "WhatsApp",
      paymentEvidenceUrl: company.paymentEvidenceUrl || "",
      subscriptionNotes: company.subscriptionNotes || ""
    });
    setApprovalModalOpen(true);
  };

  const closeApprovalModal = () => {
    if (approvingId) return;
    setApprovalModalOpen(false);
    setSelectedCompany(null);
  };

  const onApprovalFieldChange = (e) => {
    const { name, value } = e.target;

    setApprovalForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "subscriptionPlan" || name === "subscriptionCycle") {
        next.subscriptionAmount = getPlanAmount(next.subscriptionPlan, next.subscriptionCycle);
        if (next.subscriptionStart) {
          next.subscriptionEnd = computeEndDate(next.subscriptionStart, next.subscriptionCycle);
        }
      }
      if (name === "subscriptionStart") {
        next.subscriptionEnd = computeEndDate(value, next.subscriptionCycle);
      }
      return next;
    });
  };

  const handleApproveAccess = async () => {
    if (!selectedCompany?.id) return;

    if (!selectedCompany.email) {
      toast("Cannot approve account without company email", "error");
      return;
    }

    if (!approvalForm.subscriptionPlan) {
      toast("Select a subscription plan first. Add plans under Subscriptions if the list is empty.", "error");
      return;
    }

    if (!approvalForm.subscriptionStart || !approvalForm.subscriptionEnd) {
      toast("Subscription start and end dates are required", "error");
      return;
    }

    if (Number(approvalForm.subscriptionAmount) <= 0) {
      toast("Subscription amount must be greater than 0", "error");
      return;
    }

    try {
      setApprovingId(selectedCompany.id);
      const res = await api.put(`/companies/${selectedCompany.id}`, {
        companyName: selectedCompany.companyName,
        email: selectedCompany.email,
        phone: selectedCompany.phone || "",
        address: selectedCompany.address || "",
        contactPerson: selectedCompany.contactPerson || "",
        subscriptionStatus: "Active",
        subscriptionPlan: approvalForm.subscriptionPlan,
        subscriptionCycle: approvalForm.subscriptionCycle,
        subscriptionAmount: Number(approvalForm.subscriptionAmount),
        subscriptionCurrency: "GMD",
        subscriptionStart: approvalForm.subscriptionStart,
        subscriptionEnd: approvalForm.subscriptionEnd,
        paymentContactMethod: approvalForm.paymentContactMethod,
        paymentEvidenceUrl: approvalForm.paymentEvidenceUrl.trim() || null,
        subscriptionNotes: approvalForm.subscriptionNotes.trim() || null,
        active: 1
      });

      toast(res.data?.message || `${selectedCompany.companyName} approved and payment receipt sent`, "success");
      closeApprovalModal();
      await Promise.all([fetchOverview(), fetchCompanies()]);
    } catch (error) {
      console.error("Approve access error:", error);
      toast(error.response?.data?.message || "Failed to approve account", "error");
    } finally {
      setApprovingId(null);
    }
  };

  const executeDangerAction = async (type) => {
    const labels = {
      users: "delete all users except SuperAdmin",
      companies: "delete all companies and their users",
      everything: "wipe all data except SuperAdmin"
    };

    const confirmed = window.confirm(`Are you sure you want to ${labels[type]}? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      setProcessingAction(type);
      const endpoints = {
        users: "/admin/cleanup/users",
        companies: "/admin/cleanup/companies",
        everything: "/admin/cleanup/everything"
      };

      const res = await api.delete(endpoints[type]);
      toast(res.data?.message || "Action completed", "success");
      await fetchOverview();
    } catch (error) {
      console.error("SuperAdmin action error:", error);
      toast(error.response?.data?.message || "Action failed", "error");
    } finally {
      setProcessingAction("");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Super Admin Dashboard"
        action={
          <button
            type="button"
            onClick={fetchOverview}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm disabled:opacity-60"
          >
            <RefreshOutlinedIcon sx={{ fontSize: 18 }} />
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        }
      />

      <section className="rounded-2xl border border-teal-100 bg-gradient-to-r from-teal-900 via-teal-800 to-teal-700 text-white p-6">
        <p className="text-xs uppercase tracking-[0.16em] text-teal-100 font-bold">System Status</p>
        <h2 className="text-2xl md:text-3xl font-black mt-2">System Monitoring Platform</h2>
        <p className="text-teal-100 mt-2 max-w-3xl">
          Monitor company growth, user activity, operational data, and execute restricted maintenance actions.
        </p>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map((card) => (
          <article key={card.label} className={`rounded-2xl border p-4 shadow-sm bg-white ${card.tone}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.12em] font-bold opacity-80">{card.label}</p>
                <p className="text-2xl font-black text-gray-900 mt-1">{card.value}</p>
              </div>
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/80 border border-white/60">
                <card.icon sx={{ fontSize: 22 }} />
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-3">{card.hint}</p>
          </article>
        ))}
      </section>

      <section className="rounded-xl border border-amber-200 bg-amber-50 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-amber-200 flex items-center justify-between">
          <h3 className="text-base font-bold text-amber-900">Pending Access Requests</h3>
          <span className="text-xs font-semibold text-amber-800">{pendingCompanies.length} account(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-amber-100/70">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-amber-900">Company</th>
                <th className="text-left px-4 py-3 font-semibold text-amber-900">Email</th>
                <th className="text-left px-4 py-3 font-semibold text-amber-900">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-amber-900">Requested Plan</th>
                <th className="text-left px-4 py-3 font-semibold text-amber-900">Action</th>
              </tr>
            </thead>
            <tbody>
              {pendingCompanies.map((company) => (
                <tr key={company.id} className="border-t border-amber-100">
                  <td className="px-4 py-3 font-medium text-gray-900">{company.companyName}</td>
                  <td className="px-4 py-3 text-gray-700">{company.email || "-"}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {Number(company.active) === 0 ? "Awaiting approval" : company.subscriptionStatus}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {company.subscriptionPlan || "Starter"} • {company.subscriptionCycle || "Monthly"}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openApprovalModal(company)}
                      disabled={approvingId === company.id}
                      className="px-3 py-1.5 rounded-md bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
                    >
                      Review & Approve
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && pendingCompanies.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-amber-900">No pending access requests.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-amber-200 text-xs text-amber-900">
          Workflow: company signs up, submits payment evidence to Gamrent via Email, Phone, or WhatsApp, then SuperAdmin approves plan and subscription dates in GMD.
        </div>
      </section>

      {approvalModalOpen && selectedCompany && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4">
          <div className="w-full max-w-2xl max-h-[92dvh] sm:max-h-[90dvh] overflow-y-auto overscroll-contain rounded-t-3xl sm:rounded-2xl border border-gray-200 bg-white shadow-2xl pb-[env(safe-area-inset-bottom)]">
            <div className="px-6 py-4 bg-gradient-to-r from-teal-900 via-teal-800 to-teal-700 text-white flex items-center justify-between">
              <h3 className="text-lg font-black">Approve Subscription Access</h3>
              <button onClick={closeApprovalModal} className="text-white text-2xl leading-none" disabled={!!approvingId}>×</button>
            </div>

            <div className="p-6 space-y-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-sm font-semibold text-gray-900">{selectedCompany.companyName}</p>
                <p className="text-xs text-gray-600">{selectedCompany.email}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Plan</label>
                  <select
                    name="subscriptionPlan"
                    value={approvalForm.subscriptionPlan}
                    onChange={onApprovalFieldChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {subscriptionPlans.length === 0 ? (
                      <option value="">No plans configured</option>
                    ) : (
                      subscriptionPlans.filter((plan) => plan.active !== false).map((plan) => (
                        <option key={plan.id} value={plan.name}>{plan.name}</option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Billing Cycle</label>
                  <select
                    name="subscriptionCycle"
                    value={approvalForm.subscriptionCycle}
                    onChange={onApprovalFieldChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Amount (Dalasi)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">GMD</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="subscriptionAmount"
                      value={approvalForm.subscriptionAmount}
                      onChange={onApprovalFieldChange}
                      className="w-full pl-14 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Payment Contact Channel</label>
                  <select
                    name="paymentContactMethod"
                    value={approvalForm.paymentContactMethod}
                    onChange={onApprovalFieldChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="Email">Email</option>
                    <option value="Phone">Phone</option>
                    <option value="WhatsApp">WhatsApp</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Subscription Start</label>
                  <input
                    type="date"
                    name="subscriptionStart"
                    value={approvalForm.subscriptionStart}
                    onChange={onApprovalFieldChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Subscription End</label>
                  <input
                    type="date"
                    name="subscriptionEnd"
                    value={approvalForm.subscriptionEnd}
                    onChange={onApprovalFieldChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Payment Evidence Link (optional)</label>
                <input
                  type="text"
                  name="paymentEvidenceUrl"
                  value={approvalForm.paymentEvidenceUrl}
                  onChange={onApprovalFieldChange}
                  placeholder="https://..."
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Admin Notes</label>
                <textarea
                  rows="3"
                  name="subscriptionNotes"
                  value={approvalForm.subscriptionNotes}
                  onChange={onApprovalFieldChange}
                  placeholder="Payment verified from WhatsApp evidence. Activated yearly Professional plan in GMD..."
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeApprovalModal}
                disabled={!!approvingId}
                className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApproveAccess}
                disabled={!!approvingId}
                className="px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
              >
                {approvingId ? "Approving..." : "Approve and Allow Access"}
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-base font-bold text-gray-900">Recent Companies</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Company</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Email</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Created</th>
                </tr>
              </thead>
              <tbody>
                {(overview.recentCompanies || []).map((company) => (
                  <tr key={company.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-900">{company.companyName}</td>
                    <td className="px-4 py-3 text-gray-700">{company.email || "-"}</td>
                    <td className="px-4 py-3 text-gray-700">{new Date(company.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
                {!loading && (!overview.recentCompanies || overview.recentCompanies.length === 0) && (
                  <tr>
                    <td colSpan="3" className="px-4 py-8 text-center text-gray-500">No companies found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-base font-bold text-gray-900">Recent Users</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Role</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Created</th>
                </tr>
              </thead>
              <tbody>
                {(overview.recentUsers || []).map((user) => (
                  <tr key={user.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-900">{user.firstName} {user.lastName}</td>
                    <td className="px-4 py-3 text-gray-700">{user.role}</td>
                    <td className="px-4 py-3 text-gray-700">{new Date(user.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
                {!loading && (!overview.recentUsers || overview.recentUsers.length === 0) && (
                  <tr>
                    <td colSpan="3" className="px-4 py-8 text-center text-gray-500">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-red-200 bg-red-50 p-5">
        <div className="flex items-center gap-2">
          <WarningAmberOutlinedIcon className="text-red-700" />
          <h3 className="text-base font-black text-red-900">Restricted System Actions</h3>
        </div>
        <p className="text-sm text-red-800 mt-1">Use these tools only for maintenance and controlled reset operations.</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            onClick={() => executeDangerAction("users")}
            disabled={processingAction !== ""}
            className="px-4 py-3 rounded-lg border border-red-300 bg-white text-red-700 font-semibold hover:bg-red-100 disabled:opacity-60"
          >
            {processingAction === "users" ? "Processing..." : "Cleanup Users"}
          </button>
          <button
            onClick={() => executeDangerAction("companies")}
            disabled={processingAction !== ""}
            className="px-4 py-3 rounded-lg border border-red-300 bg-white text-red-700 font-semibold hover:bg-red-100 disabled:opacity-60"
          >
            {processingAction === "companies" ? "Processing..." : "Cleanup Companies"}
          </button>
          <button
            onClick={() => executeDangerAction("everything")}
            disabled={processingAction !== ""}
            className="px-4 py-3 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-60"
          >
            {processingAction === "everything" ? "Processing..." : "Nuclear Reset"}
          </button>
        </div>
      </section>
    </div>
  );
};

export default SuperAdminDashboard;
